import type { ReactNode } from 'react';
import {
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type {
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { sortFormTypeCatalogByPrecedence } from './formPrecedence';
import { isEffective } from './formSchemaUtils';

export function SchemaVersionBrowser({
  formTypeCatalog,
  catalogLoading,
  selectedRogerDocFormType,
  onFormTypeChange,
  versions,
  versionsLoading,
  versionsError,
  selectedVersion,
  onVersionSelect,
  actions,
}: {
  formTypeCatalog: RogerDocFormTypeCatalogEntry[];
  catalogLoading: boolean;
  selectedRogerDocFormType: RogerDocFormType | null;
  onFormTypeChange: (formType: RogerDocFormType) => void;
  versions: FormSchemaVersion[];
  versionsLoading: boolean;
  versionsError: string | null;
  selectedVersion: FormSchemaVersion | null;
  onVersionSelect: (version: FormSchemaVersion) => void;
  /** Page-wide actions, shown at the right of the form type row. */
  actions?: ReactNode;
}) {
  return (
    <>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
        <div className='max-w-xs flex-1 min-w-[12rem]'>
          <label className='mb-1 block text-sm font-medium'>Form Type</label>
          <Select
            value={selectedRogerDocFormType ?? ''}
            onValueChange={v => onFormTypeChange(v as RogerDocFormType)}
            disabled={catalogLoading || formTypeCatalog.length === 0}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  catalogLoading ? 'Loading form types…' : 'No form types'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {sortFormTypeCatalogByPrecedence(formTypeCatalog).map(row => (
                <SelectItem key={row.form_type} value={row.form_type}>
                  {row.display_label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {actions}
      </div>

      <div className='border-y border-border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='py-1.5'>Name</TableHead>
              <TableHead className='py-1.5'>Effective From</TableHead>
              <TableHead className='py-1.5'>Effective Until</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {versionsLoading ? (
              <TableRow>
                <TableCell colSpan={3} className='text-center py-8'>
                  <Loader2 className='h-5 w-5 animate-spin mx-auto' />
                </TableCell>
              </TableRow>
            ) : versionsError ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className='text-center py-8 text-destructive'
                >
                  Could not load schema versions for this form type.
                  <span className='mt-1 block text-sm text-muted-foreground'>
                    {versionsError}
                  </span>
                </TableCell>
              </TableRow>
            ) : versions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className='text-center py-8 text-muted-foreground'
                >
                  No schema versions found for this visit type.
                </TableCell>
              </TableRow>
            ) : (
              versions.map(v => {
                const active = isEffective(v);
                const versionKey = `${v.name}::${v.version ?? ''}`;
                const selectedKey = selectedVersion
                  ? `${selectedVersion.name}::${selectedVersion.version ?? ''}`
                  : null;
                const selected = selectedKey === versionKey;
                return (
                  <TableRow
                    key={versionKey}
                    className={`cursor-pointer ${selected ? 'bg-muted' : 'hover:bg-muted/50'}`}
                    onClick={() => onVersionSelect(v)}
                  >
                    <TableCell className='font-medium py-1.5'>
                      {v.name}
                      {active && (
                        <Badge className='ml-2 text-xs' variant='default'>
                          Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className='py-1.5'>
                      {v.effective_from
                        ? formatDateForDisplay(v.effective_from) || '—'
                        : '—'}
                    </TableCell>
                    <TableCell className='py-1.5'>
                      {v.effective_until
                        ? formatDateForDisplay(v.effective_until) || '—'
                        : '—'}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
